import React, { useState } from 'react';
import { AddressBook, Bank, Buildings, DeviceMobile, DownloadSimple, Export, FileCsv, Funnel, MapPin, MicrosoftExcelLogo, PaperPlaneTilt, Users, Wallet, type Icon } from '@phosphor-icons/react';
import { ImmoButton, InnenKarte, KartenTitel, ListenPanel, SchalterZeile, SegmentedControl } from '../../../../components/immobilien/ui';
import { useNotification } from '../../../../contexts/NotificationContext';
import { exportCsv } from '../../../../lib/export/exportCsv';
import { exportXlsx } from '../../../../lib/export/exportXlsx';
import { brauchtBeteiligte, EXPORT_GRUPPEN, exportKonfig, standardGruppen, type EigExportRoh, type ExportGruppe } from '../../../../lib/eigentuemer/export';
import type { EigZeile } from '../../../../lib/eigentuemer/liste';
import { ladeExportBeteiligte } from '../../../../lib/eigentuemer/useEigentuemerListe';

const GRUPPEN_ICON: Record<ExportGruppe, Icon> = {
  stammdaten: AddressBook, adresse: MapPin, bank: Bank, objekte: Buildings, konto: Wallet, portal: DeviceMobile, zustellung: PaperPlaneTilt,
};

type Format = 'xlsx' | 'csv';
type Umfang = 'ansicht' | 'alle';

export function ExportPanel({ ansicht, alle, roh, heute, onSchliessen }: {
  /** gefilterte Zeilen (alle Seiten) */
  ansicht: readonly EigZeile[];
  alle: readonly EigZeile[];
  roh: ReadonlyMap<string, EigExportRoh>;
  heute: string;
  onSchliessen: () => void;
}) {
  const { showToast } = useNotification();
  const [format, setFormat] = useState<Format>('xlsx');
  const [umfang, setUmfang] = useState<Umfang>('ansicht');
  const [gruppen, setGruppen] = useState<ReadonlySet<ExportGruppe>>(standardGruppen);
  const zeilen = umfang === 'ansicht' ? ansicht : alle;

  const schalte = (g: ExportGruppe) => (an: boolean) => setGruppen(prev => {
    const n = new Set(prev);
    if (an) n.add(g); else n.delete(g);
    return n;
  });

  const [laeuft, setLaeuft] = useState(false);
  const exportieren = async () => {
    setLaeuft(true);
    try {
      const beteiligte = brauchtBeteiligte(gruppen) ? await ladeExportBeteiligte(zeilen.map(z => z.id)) : undefined;
      const konfig = exportKonfig(zeilen, roh, gruppen, heute, beteiligte);
      if (format === 'csv') exportCsv(konfig); else exportXlsx(konfig);
      showToast({ type: 'success', message: `${konfig.fileName}.${format} mit ${konfig.data.length} Zeilen erstellt` });
      onSchliessen();
    } catch (e) {
      showToast({ type: 'error', message: `Export fehlgeschlagen: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}` });
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <ListenPanel icon={Export} titel="Export" sub="Eigentümerliste als Excel oder CSV" onSchliessen={onSchliessen}>
      <InnenKarte>
        <KartenTitel>Format &amp; Umfang</KartenTitel>
        <SegmentedControl<Format> ariaLabel="Format" wert={format} onChange={setFormat} className="self-start"
          segmente={[{ key: 'xlsx', label: 'Excel (.xlsx)', icon: MicrosoftExcelLogo }, { key: 'csv', label: 'CSV (;)', icon: FileCsv }]} />
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 8 }}>
          {EXPORT_GRUPPEN.map(g => (
            <SchalterZeile key={g.key} icon={GRUPPEN_ICON[g.key]} label={g.label} an={gruppen.has(g.key)} onChange={schalte(g.key)}
              zusatz={g.key === 'konto' ? 'Saldo startet mit der Buchhaltung' : undefined} />
          ))}
        </div>
      </InnenKarte>
      <InnenKarte>
        <KartenTitel>Welche Eigentümer?</KartenTitel>
        <div className="flex flex-wrap items-center justify-between" style={{ gap: 12 }}>
          <SegmentedControl<Umfang> ariaLabel="Umfang" wert={umfang} onChange={setUmfang}
            segmente={[{ key: 'ansicht', label: 'Aktuelle Ansicht', icon: Funnel }, { key: 'alle', label: 'Alle', icon: Users }]} />
          <ImmoButton art="akzent" groesse={44} icon={DownloadSimple} onClick={() => { void exportieren(); }} disabled={laeuft || zeilen.length === 0 || gruppen.size === 0}>
            {laeuft ? 'Export wird erstellt …' : `${zeilen.length} Eigentümer exportieren`}
          </ImmoButton>
        </div>
      </InnenKarte>
    </ListenPanel>
  );
}
